import { lazy, Suspense, type ReactNode } from 'react';

import { useMobile } from '@/shared/lib/use-mobile';
import { retryImport } from '@/shared/lib/lazy-with-retry';

/**
 * Top-level shell selector.  Renders MobileShell below 768px, otherwise
 * DesktopShell.  Same routes, same data — different chrome around the
 * page content.
 *
 * Each shell is lazy-loaded so a phone never downloads desktop-only chrome
 * (Sidebar / Header / CommandPalette / hotkeys) and a desktop never downloads
 * the mobile sheet stack + bottom-nav. Only the selected branch's chunk is
 * fetched. Everything load-bearing (auth bootstrap/guard, realtime + toast
 * hooks, providers) lives ABOVE `<Shell>` in `__root.tsx` and stays eager, so
 * this split is purely downstream chrome.
 *
 * Architecture: `docs/mobile/08-ARCHITECTURE.md §1`.
 */
const DesktopShell = lazy(() =>
  retryImport(() => import('./desktop/desktop-shell')).then((m) => ({ default: m.DesktopShell })),
);
const MobileShell = lazy(() =>
  retryImport(() => import('./mobile/mobile-shell')).then((m) => ({ default: m.MobileShell })),
);

export function Shell({ children }: { children: ReactNode }) {
  const isMobile = useMobile();
  return (
    // Neutral full-height fallback covers the (one-time, cached) shell fetch so
    // there's no flash of unstyled content while the chrome chunk loads.
    <Suspense fallback={<div className="h-[100dvh] bg-[var(--bg)]" />}>
      {isMobile ? <MobileShell>{children}</MobileShell> : <DesktopShell>{children}</DesktopShell>}
    </Suspense>
  );
}
